import { splitCreatesForTrial } from './subscriptionPlan.js';

/**
 * Dispatch a multi-cycle checkout plan against injected grpc functions.
 * Returns billingState observed across all calls (for the route handler to
 * shape the HTTP response). Pure orchestration — no Stripe SDK / network
 * imports here. Test by injecting mock grpc functions.
 *
 * @param {object} args
 * @param {object} args.plan — output of resolveCheckoutUpdates
 * @param {object} args.body — POST body (tolt_referral, coupon)
 * @param {object[]} args.subscriptions
 * @param {object} args.catalog
 * @param {{ usedProductIds?: string[], skipTrial?: boolean }} [args.trialContext]
 *   Per-create trial inputs. Each create bucket is split (splitCreatesForTrial)
 *   so trial-eligible items get their OWN trial sub and everything else a paid
 *   sub — a trial product never shares a subscription with a paid one, and an
 *   unrelated cart line can't veto a brand-new trial-eligible sub.
 * @param {{
 *   modifySubscription: (req) => Promise<any>,
 *   addSubscriptionItem: (req) => Promise<any>,
 *   createSubscription: (req) => Promise<any>,
 * }} args.grpc
 */
export async function executeCheckoutPlan({ plan, body, subscriptions, catalog, grpc, trialContext }) {
  const usedProductIds = trialContext?.usedProductIds || [];
  const skipTrial = Boolean(trialContext?.skipTrial);
  if (plan.type === 'noop') {
    return {
      mode: 'none',
      subscriptionId: subscriptions[0]?.id,
      status: 'unchanged',
      calls: { modifies: 0, adds: 0, creates: 0 }
    };
  }
  if (plan.type !== 'multi-cycle') {
    throw new Error(`Unsupported plan type: ${plan.type}`);
  }

  // Affiliate attribution rides CREATE ops only — it belongs to the birth of a
  // subscription, and re-stamping it on a later seat bump or addon add would
  // re-attribute an existing subscription to whoever's referral cookie happens
  // to be set. ModifySubscription/AddSubscriptionItem carry no tolt_referral
  // field at all, so this is enforced by the wire contract, not just here.
  const toltReferral = body.tolt_referral ?? body.toltReferral ?? '';
  // Stripe coupon NAME the customer typed (e.g. SAVE30). Unlike the referral
  // above, this rides EVERY purchase op: creates apply it at subscription
  // level, adds/modifies attach it at item level to the changed line only —
  // billing-manager resolves the name and scopes the discount server-side, so
  // a code works on any new checkout no matter which subscription shape the
  // cart lands on. Removes never carry it.
  const coupon = body.coupon ?? '';
  // One ref per executor run = one checkout. billing-manager's once-per-
  // customer redemption ledger scopes by it, so the several purchase ops of
  // this cart (seat bump + add + create buckets) count as a single redemption
  // instead of the first op's ledger row blocking the discount on the rest.
  const checkoutRef =
    body.checkout_ref ??
    body.checkoutRef ??
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `ck_${Date.now()}_${Math.random().toString(36).slice(2)}`);

  const billingState = {
    lastPi: null,
    subscriptionId: null,
    lastClientSecret: null
  };
  const calls = { modifies: 0, adds: 0, creates: 0 };
  // Subscriptions this run actually brought into existence, in creation order.
  // Deliberately separate from `billingState.subscriptionId`, which is
  // last-write-wins across modifies/adds/creates/removes and therefore ends up
  // holding an EXISTING subscription id whenever the cart also touches one.
  // Anything that needs "what did this checkout create" (conversion tracking)
  // must read this, never `subscriptionId`.
  const createdSubscriptions = [];

  /** @returns {string|undefined} the subscription id the reply carried, if any. */
  function captureReply(reply) {
    if (!reply || reply.skipped) return;
    // grpc-web `resp.toObject()` returns camelCase fields. Older snake_case
    // accesses silently no-op, leaving `lastClientSecret` undefined and the
    // payment confirmation step skipped — which strands new-cycle subs in
    // Stripe's `incomplete` state forever.
    const pi = reply.paymentIntent ?? reply.payment_intent;
    const piSecret = pi?.clientSecret ?? pi?.client_secret;
    if (piSecret) {
      billingState.lastPi = pi;
      billingState.lastClientSecret = piSecret;
    }
    const replySecret = reply.clientSecret ?? reply.client_secret;
    if (replySecret) billingState.lastClientSecret = replySecret;
    const replySubId = reply.subscriptionId ?? reply.subscription_id;
    if (replySubId) billingState.subscriptionId = replySubId;
    return replySubId;
  }

  for (const op of plan.ops) {
    for (const mod of op.modifies || []) {
      // SKU swap (e.g. futures pay-as-you-go → unlimited): the planner sets
      // `mod.priceId` when the product stays the same but the price changes.
      // ModifySubscription has NO price field (proto carries only qty + item
      // id), so an in-place price swap is impossible. Perform it as
      // AddSubscriptionItem(new SKU) + RemoveSubscriptionItem(old line).
      //
      // Order: add FIRST, then remove — this never leaves the sub without a
      // line (BE rejects removing the sole item, which would break single-line
      // futures subs) and routes the prorated charge through the add. The two
      // RPCs are NOT atomic: if the remove fails after a successful add the sub
      // is briefly left with both lines (recoverable, surfaced as a thrown
      // error). A fully atomic swap needs BE support (price_id on
      // ModifySubscription or a dedicated swap RPC). The `typeof` guard keeps
      // adapters that don't supply removeSubscriptionItem on the legacy path.
      if (mod.priceId && mod.subscriptionItemId && typeof grpc.removeSubscriptionItem === 'function') {
        // AddSubscriptionItem expects the same price-id shape as the planner's
        // `adds` (catalog id). The planner stored the Stripe id on `mod.priceId`
        // for the preview RPC; the catalog is indexed by both ids, so resolve
        // the entry and prefer its catalog `id`, falling back to the raw value.
        const desired = catalog?.getById?.(mod.priceId);
        const addPriceId = desired?.id || mod.priceId;
        const addReply = await grpc.addSubscriptionItem({
          subscription_id: mod.subscriptionId,
          price_id: addPriceId,
          quantity: mod.quantity,
          coupon,
          checkout_ref: checkoutRef
        });
        calls.adds += 1;
        captureReply(addReply);
        await grpc.removeSubscriptionItem({
          subscription_id: mod.subscriptionId,
          subscription_item_id: mod.subscriptionItemId
        });
        billingState.subscriptionId = mod.subscriptionId;
        continue;
      }

      const reply = await grpc.modifySubscription({
        subscription_id: mod.subscriptionId,
        quantity: mod.quantity,
        subscription_item_id: mod.subscriptionItemId ?? '',
        coupon,
        checkout_ref: checkoutRef
      });
      calls.modifies += 1;
      const modPi = reply?.paymentIntent ?? reply?.payment_intent;
      if (modPi) billingState.lastPi = modPi;
      const modPiSecret = billingState.lastPi?.clientSecret ?? billingState.lastPi?.client_secret;
      if (modPiSecret) {
        billingState.lastClientSecret = modPiSecret;
      }
      billingState.subscriptionId = mod.subscriptionId;
    }

    for (const item of op.adds || []) {
      // Per-add `sub_id` lets DE route to its own host sub (legacy DE-only or
      // carrier) while non-DE adds keep falling back to the cycle anchor.
      // The planner's ownerSubForProductOnCycle already prevents duplicate
      // adds to a sub that already has that product line — no global product
      // dedupe needed here, and a global check wrongly blocks legitimate
      // cross-cycle adds (e.g. prem on monthly + prem on quarterly).
      const reply = await grpc.addSubscriptionItem({
        subscription_id: item.sub_id || op.subId,
        price_id: item.price_id,
        quantity: item.quantity,
        coupon,
        checkout_ref: checkoutRef
      });
      calls.adds += 1;
      captureReply(reply);
    }

    if ((op.creates || []).length > 0) {
      // Split so a trial-eligible product (Futures) gets its OWN subscription and
      // never shares one with a paid product — Stripe trial_end is sub-level, so
      // bundling would either free the paid item (revenue loss) or make BM reject
      // the whole sub. Trial items → one trial sub; everything else → one paid
      // sub. `splitCreatesForTrial` already honours skipTrial / used-set / DE.
      const { trialItems, paidItems } = splitCreatesForTrial(op.creates, catalog, usedProductIds, skipTrial, Boolean(op.isDedi));

      if (trialItems.length > 0) {
        // Coupon rides on trial create-buckets too. billing-manager applies it
        // at subscription creation but deliberately does NOT apply the discount
        // to trial creates — a duration=once coupon would burn on the $0 trial
        // invoice; the backend currently ignores it there. Sent anyway so the
        // wire shape stays uniform and future backend use needs no FE change.
        const reply = await grpc.createSubscription({
          items: trialItems,
          use_trial: true,
          tolt_referral: toltReferral,
          coupon,
          checkout_ref: checkoutRef,
          is_dedi: false
        });
        calls.creates += 1;
        // captureReply returns undefined for a skipped (ALREADY_EXISTS) reply,
        // so a no-op create never lands here and never reads as a purchase.
        const createdId = captureReply(reply);
        if (createdId) createdSubscriptions.push({ id: createdId, isTrial: true });
      }

      if (paidItems.length > 0) {
        const reply = await grpc.createSubscription({
          items: paidItems,
          use_trial: false,
          tolt_referral: toltReferral,
          coupon,
          checkout_ref: checkoutRef,
          is_dedi: Boolean(op.isDedi)
        });
        calls.creates += 1;
        const createdId = captureReply(reply);
        if (createdId) createdSubscriptions.push({ id: createdId, isTrial: false });
      }
    }

    // Cancellations LAST: modifies above already moved kept seats onto the
    // surviving line, so removing the legacy line never strands the sub (BE
    // rejects removing the sole item). Not atomic — a failure leaves it in place.
    for (const rm of op.removes || []) {
      if (typeof grpc.removeSubscriptionItem !== 'function') break;
      const reply = await grpc.removeSubscriptionItem({
        subscription_id: rm.subscriptionId || op.subId,
        subscription_item_id: rm.subscriptionItemId
      });
      calls.removes = (calls.removes || 0) + 1;
      captureReply(reply);
      billingState.subscriptionId = rm.subscriptionId || op.subId;
    }
  }

  return { billingState, calls, createdSubscriptions, checkoutRef };
}
